import * as React from "react";
import { useImperativeHandle, useRef } from "react";
import {
  CircularProgress,
  Popper,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FARTICLE_URL } from "../../../utils/UrlsUtils";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { objectToQuery, requestApi } from "../../../helpers/RequestApi";
import { GET } from "../../../utils/MethodUtils";
import getErrorApi from "../../../helpers/GetErrorApi";
import { toastr } from "react-redux-toastr";
import {
  DriverInterface,
  FArticleSmallInterface,
} from "../../../interfaces/FArticleInterface";
import { Link, useNavigate } from "react-router-dom";
import { CATEGORY_PAGE, PRODUCTS_PAGE } from "../../../utils/RouteUtils";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import { STORAGE_INDEXABLE } from "../../../utils/StorageUtils";
import { ELASTIC_STOCK_AVAILABLE } from "../../../utils/FArticleUtils";
import { FArticleSmallComponentAutocomplete } from "./FArticleSmallComponent";
import Typography from "@mui/material/Typography";
import { CategoryInterface } from "../../../interfaces/CategoryInterface";
import {
  getUrlCategoryImage,
  getUrlConstructorImage,
} from "../../../helpers/FileHelper";
import { IMAGE_SIZE_LOGO } from "../../../utils/FileUtils";
import HideImageIcon from "@mui/icons-material/HideImage";

const stringSimilarity = require("string-similarity");
const slugify = require("slugify");

interface State {
  onSuccess: Function;
  handleSearchClose?: Function;
  hideSearchIcon?: boolean;
  hideConstructor?: boolean;
  preventSearch?: boolean;
  full?: boolean;
  largeArRef?: boolean;
}

const minStringSimilarity = 0.8;

interface CategoryCount {
  category: CategoryInterface;
  count: number;
}

interface BrandCount {
  brand: DriverInterface;
  count: number;
}

const SearchFArticleComponent = React.memo(
  React.forwardRef(
    (
      {
        onSuccess,
        handleSearchClose,
        hideSearchIcon,
        hideConstructor,
        preventSearch,
        largeArRef,
        full,
      }: State,
      ref
    ) => {
      const theme = useTheme();
      const [isFocused, setIsFocused] = React.useState(false);
      const inputRef: any = useRef();
      const popperRef = useRef<any>(null);
      const categories = useAppSelector(
        (state: RootState) => state.globalState.categories
      );
      const isSmall = useMediaQuery(theme.breakpoints.down("md"));
      const { t } = useTranslation();
      const drivers = useAppSelector(
        (state: RootState) => state.globalState.drivers
      );
      const [searchFArticle, setSearchFArticle] = React.useState<string>("");
      const [showCategories, setShowCategories] = React.useState<
        CategoryCount[]
      >([]);
      const [showBrands, setShowBrands] = React.useState<BrandCount[]>([]);
      const [isTyping, setIsTyping] = React.useState<boolean>(false);
      const [isSearching, setIsSearching] = React.useState<boolean>(false);
      const token = useAppSelector(
        (state: RootState) => state.globalState.token
      );
      const isAdmin = useAppSelector(
        (state: RootState) => state.globalState.isAdmin
      );
      const globalSearchByRayon = useAppSelector(
        (state: RootState) => state.globalState.globalSearchByRayon
      );
      const [fArticles, setFArticles] = React.useState<
        FArticleSmallInterface[]
      >([]);
      const getDriverNames = React.useCallback(() => {
        return (
          drivers?.map((driver) => slugify(driver.name, { lower: true })) ?? []
        );
      }, [drivers]);
      const [driverNames, setDriverNames] = React.useState<string[]>(
        getDriverNames()
      );
      const navigate = useNavigate();

      const goToSearchFArticle = React.useCallback(
        (keywords: string) => {
          let driverFound = "";
          let driver = null;
          const listKeywords = keywords
            .split(" ")
            .filter((keyword) => keyword !== "");
          for (const keyword of listKeywords) {
            const matchResult = stringSimilarity.findBestMatch(
              slugify(keyword, { lower: true }),
              driverNames
            );
            if (matchResult.bestMatch.rating > minStringSimilarity) {
              driver = drivers?.find(
                (driver) =>
                  slugify(driver.name, { lower: true }) ===
                  matchResult.bestMatch.target
              );
              driverFound = keyword;
              break;
            }
          }
          // if (driver) {
          //   navigate(
          //     PRODUCTS_PAGE +
          //       objectToQuery({
          //         "brands[]": driver.name,
          //         keywords: keywords.replace(driverFound, ""),
          //       })
          //   );
          // } else {
          navigate(
            PRODUCTS_PAGE +
              objectToQuery({
                keywords: keywords,
                single: "true",
                ...(globalSearchByRayon && {
                  "order[categories]": JSON.stringify({
                    order: "asc",
                  }),
                }),
              })
          );
          // }
          setSearchFArticle("");
          setFArticles([]);
          if (handleSearchClose) {
            handleSearchClose();
          }
        },
        [driverNames, drivers, globalSearchByRayon, handleSearchClose, navigate]
      );

      const getCategories = React.useCallback(
        (newFArticles: FArticleSmallInterface[]) => {
          const result: CategoryCount[] = [];
          if (!categories) {
            return result;
          }
          for (const fArticle of newFArticles) {
            for (const fArticleCategory of fArticle?.categories ?? []) {
              for (const category1 of categories) {
                for (const category2 of category1.children ?? []) {
                  if (category2.id === fArticleCategory.id) {
                    let c = result.find(
                      (x) => x.category.id === fArticleCategory.id
                    );
                    if (!c) {
                      c = {
                        category: category2,
                        count: 0,
                      };
                      result.push(c);
                    }
                    c.count++;
                  }
                }
              }
            }
          }
          return result.sort((x, y) => y.count - x.count);
        },
        [categories]
      );

      const getBrands = React.useCallback(
        (newFArticles: FArticleSmallInterface[]) => {
          const result: BrandCount[] = [];
          const showCategories = getCategories(newFArticles);
          for (const category of showCategories) {
            for (const brandName of category.category.brands ?? []) {
              let b = result.find((x) => x.brand.name === brandName);
              if (!b) {
                const thisBrand = drivers?.find((x) => x.name === brandName);
                if (thisBrand) {
                  b = {
                    brand: thisBrand,
                    count: 0,
                  };
                  result.push(b);
                }
              }
              if (b) {
                b.count++;
              }
            }
          }
          return result.sort((x, y) => y.count - x.count).slice(0, 20);
        },
        [drivers, getCategories]
      );

      const searchFArticles = React.useCallback(async () => {
        const keywords = searchFArticle.trim();
        setIsTyping(false);
        if (keywords === "") {
          return;
        }
        setIsSearching(true);
        let indexable = localStorage.getItem(STORAGE_INDEXABLE);
        if (indexable === null) {
          indexable = "1";
        }
        const param: any = {
          keywords: keywords,
          ...(!isAdmin && {
            stock: ELASTIC_STOCK_AVAILABLE,
          }),
          ...(largeArRef && {
            largeArRef: true,
          }),
          ...(indexable !== "" && { indexable: indexable }),
          ...(full && { full: true }),
        };
        const response = await requestApi({
          method: GET,
          path: FARTICLE_URL + objectToQuery(param),
          allowError: false,
          paginate: false,
          token: token,
          timeout: 30_000,
        });
        if (searchFArticle !== inputRef?.current?.value) {
          // search has changed don't use this result
          return;
        }
        if (response.statusCode === 200) {
          const matchResult = stringSimilarity.findBestMatch(
            slugify(keywords, { lower: true }),
            driverNames
          );
          let result = response.content.fArticles;
          if (hideConstructor !== true) {
            if (matchResult.bestMatch.rating > minStringSimilarity) {
              const driver = drivers?.find(
                (driver) =>
                  slugify(driver.name, { lower: true }) ===
                  matchResult.bestMatch.target
              );
              if (driver) {
                result = [driver, ...result];
              }
            }
          }
          setFArticles(result);
          setShowCategories(getCategories(result));
          setShowBrands(getBrands(result));
        } else if (response.statusCode === 401) {
          toastr.info(t("word.info"), t("error.reconnect"));
        } else {
          for (let message of getErrorApi(response.content)) {
            toastr.error(t("word.error"), t(message));
          }
        }
        setIsSearching(false);
      }, [
        driverNames,
        drivers,
        full,
        getBrands,
        getCategories,
        hideConstructor,
        isAdmin,
        largeArRef,
        searchFArticle,
        t,
        token,
      ]);

      useImperativeHandle(ref, () => ({
        focus() {
          inputRef.current.focus();
        },
      }));

      React.useEffect(() => {
        setDriverNames(getDriverNames());
      }, [drivers]); // eslint-disable-line react-hooks/exhaustive-deps

      React.useEffect(() => {
        if (searchFArticle !== "") {
          setIsTyping(true);
        }
        const timeoutTyping = setTimeout(() => {
          searchFArticles();
        }, 500);
        return () => clearTimeout(timeoutTyping);
      }, [searchFArticle]); // eslint-disable-line react-hooks/exhaustive-deps

      React.useEffect(() => {
        function handleClickOutside(event: any) {
          if (
            inputRef.current &&
            !inputRef.current.contains(event.target) &&
            popperRef.current &&
            !popperRef.current.contains(event.target)
          ) {
            setIsFocused(false);
          }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }, []);

      const maxHeight = 500;
      return (
        <>
          {isSmall && searchFArticle.trim() !== "" && (
            <Button
              variant="contained"
              onClick={() => goToSearchFArticle(searchFArticle.trim())}
              sx={{
                paddingY: 2,
                width: "100%",
              }}
            >
              {t("word.search")}
            </Button>
          )}
          <TextField
            value={searchFArticle}
            onChange={(e) => setSearchFArticle(e.target.value)}
            placeholder="Rechercher..."
            fullWidth
            inputRef={inputRef}
            onFocus={() => setIsFocused(true)}
            onKeyUp={(e) => {
              if (e.key === "Enter" && preventSearch !== true) {
                goToSearchFArticle(searchFArticle.trim());
              }
            }}
            autoComplete="off"
            variant="outlined"
            InputProps={{
              sx: {
                padding: "4px 8px",
                "& input": {
                  padding: 0,
                },
              },
            }}
            sx={{
              bgcolor: "background.paper",
            }}
          />
          {!isTyping && searchFArticle.trim() !== "" && (
            <Popper
              open={isFocused}
              anchorEl={inputRef.current}
              placement="bottom-start"
              modifiers={[
                {
                  name: "offset",
                  options: { offset: [0, 4] },
                },
              ]}
              style={{
                zIndex: 1199,
                width: "80vw",
                display: "flex",
                backgroundColor: "white",
                border: "1px solid #ccc",
              }}
              ref={popperRef}
            >
              {isSearching ? (
                <Box sx={{ textAlign: "center", width: "100%", paddingY: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      maxHeight,
                      overflowY: "auto",
                      flex: 1,
                    }}
                  >
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {fArticles.map((option, indexOption) => (
                        <li key={indexOption}>
                          {option?.arRef && (
                            <Box
                              sx={{
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setIsFocused(false);
                                onSuccess(option);
                              }} // optional close on click
                            >
                              <FArticleSmallComponentAutocomplete
                                fArticle={option}
                                searchFArticle={searchFArticle}
                              />
                            </Box>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Box>
                  <Box sx={{ display: "flex", flex: 1 }}>
                    <Box
                      sx={{
                        maxHeight,
                        overflowY: "auto",
                        flex: 1,
                      }}
                    >
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {showCategories?.map((childCategory, indexOption) => (
                          <li
                            key={indexOption}
                            style={{ marginBottom: 4, textAlign: "center" }}
                          >
                            <Link
                              to={
                                CATEGORY_PAGE +
                                "/" +
                                childCategory.category.slug
                              }
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                            >
                              <img
                                src={
                                  (process.env.REACT_APP_API_URL ?? "") +
                                  getUrlCategoryImage(
                                    childCategory.category,
                                    IMAGE_SIZE_LOGO
                                  )
                                }
                                style={{
                                  maxWidth: "100%",
                                  margin: "auto",
                                }}
                                alt={
                                  "[" +
                                  childCategory.category.id +
                                  "] " +
                                  childCategory.category.name
                                }
                                loading="lazy"
                                referrerPolicy="no-referrer"
                              />
                              <Typography sx={{ textAlign: "center" }}>
                                {childCategory.category.name}
                              </Typography>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </Box>
                    <Box
                      sx={{
                        maxHeight,
                        overflowY: "auto",
                        flex: 1,
                      }}
                    >
                      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                        {showBrands?.map((brand, indexBrand) => (
                          <li
                            key={indexBrand}
                            style={{ marginBottom: 4, textAlign: "center" }}
                          >
                            <Link
                              to={
                                PRODUCTS_PAGE +
                                objectToQuery({
                                  "brands[]": brand.brand.name.toUpperCase(),
                                })
                              }
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                            >
                              {brand.brand.images ? (
                                <img
                                  src={
                                    (process.env.REACT_APP_API_URL ?? "") +
                                      getUrlConstructorImage(
                                        brand.brand,
                                        IMAGE_SIZE_LOGO
                                      ) ?? ""
                                  }
                                  style={{
                                    maxWidth: "100%",
                                    margin: "auto",
                                    display: "block",
                                  }}
                                  alt={brand.brand.name}
                                  loading="lazy"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <HideImageIcon
                                  sx={{
                                    width: IMAGE_SIZE_LOGO,
                                    height: IMAGE_SIZE_LOGO,
                                  }}
                                />
                              )}
                              <Typography sx={{ textAlign: "center" }}>
                                {brand.brand.name}
                              </Typography>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  </Box>
                </>
              )}
            </Popper>
          )}
          {!isSmall && hideSearchIcon !== true && (
            <Button
              variant="contained"
              onClick={() => goToSearchFArticle(searchFArticle.trim())}
              disabled={searchFArticle.trim() === ""}
            >
              <SearchIcon />
            </Button>
          )}
        </>
      );
    }
  )
);

export default SearchFArticleComponent;
